import { randomUUID } from "../utils/uuid";
import type { HistoryTrackerEntry, HistoryTrackerState } from "./history-tracker.store";

interface HistoryStateReconciliationInput extends Pick<HistoryTrackerState, "currentEntryId" | "url" | "entries"> {
  newPreviousEntryId: string | null;
  newCurrentEntryId: string | null;
  newUrl: string;
  newState: Record<string, unknown> | null;
}

export const reconcileHistoryState = (
  input: HistoryStateReconciliationInput,
): Pick<HistoryTrackerState, "currentEntryId" | "previousEntryId" | "state" | "url" | "entries"> | null => {
  if (input.currentEntryId === null) {
    if (input.newCurrentEntryId === null) {
      return historyStateForNewJourney(input);
    }

    return historyStateImmediatelyAfterRefresh(input);
  }

  if (input.newCurrentEntryId === null || input.newCurrentEntryId === input.currentEntryId) {
    if (input.newUrl === input.url) {
      return null;
    }

    return historyStateAfterDirectNavigation(input);
  }

  const existingEntry = input.entries.find(({ id }) => id === input.newCurrentEntryId);
  if (existingEntry) {
    return historyStateAfterJumpToKnownEntry(input, existingEntry);
  }

  return historyStateAfterJumpToUnknownEntry(input);
};

const historyStateForNewJourney = (input: HistoryStateReconciliationInput) => {
  const id = randomUUID();

  return {
    currentEntryId: id,
    previousEntryId: null,
    state: input.newState,
    url: input.newUrl,
    entries: [
      {
        id,
        previousId: null,
        state: input.newState,
        url: input.newUrl,
      },
    ],
  };
};

const historyStateImmediatelyAfterRefresh = (input: HistoryStateReconciliationInput) => {
  return {
    currentEntryId: input.newCurrentEntryId,
    previousEntryId: input.newPreviousEntryId,
    state: input.newState,
    url: input.newUrl,
    entries: [
      {
        id: input.newCurrentEntryId!,
        previousId: input.newPreviousEntryId,
        state: input.newState,
        url: input.newUrl,
      },
    ],
  };
};

const historyStateAfterDirectNavigation = (input: HistoryStateReconciliationInput) => {
  const id = randomUUID();
  const previousId = input.currentEntryId;

  return {
    currentEntryId: id,
    previousEntryId: previousId,
    state: {},
    url: input.newUrl,
    entries: pruneLeftoverBranch(
      [
        ...input.entries,
        {
          id,
          previousId,
          state: {},
          url: input.newUrl,
        },
      ],
      previousId,
      id,
    ),
  };
};

const historyStateAfterJumpToKnownEntry = (input: HistoryStateReconciliationInput, entry: HistoryTrackerEntry) => {
  return {
    currentEntryId: entry.id,
    previousEntryId: entry.previousId,
    state: entry.state,
    url: entry.url,
    entries: input.entries,
  };
};

const historyStateAfterJumpToUnknownEntry = (input: HistoryStateReconciliationInput) => {
  return {
    currentEntryId: input.newCurrentEntryId,
    previousEntryId: input.newPreviousEntryId,
    state: input.newState,
    url: input.newUrl,
    entries: [
      ...input.entries,
      {
        id: input.newCurrentEntryId!,
        previousId: input.newPreviousEntryId,
        state: input.newState,
        url: input.newUrl,
      },
    ],
  };
};

const pruneLeftoverBranch = (entries: HistoryTrackerEntry[], pruneFromId: string | null, skipId: string | null) => {
  if (!pruneFromId) {
    return entries;
  }

  let prunedEntries = [...entries];
  let idToPrune = prunedEntries.find((entry) => entry.previousId === pruneFromId && entry.id !== skipId)?.id;

  while (idToPrune) {
    const nextIdToPrune = prunedEntries.find((entry) => entry.previousId === idToPrune)?.id;
    prunedEntries = prunedEntries.filter((entry) => entry.id !== idToPrune);

    idToPrune = nextIdToPrune;
  }

  return prunedEntries;
};
